import React, { useState, useEffect, useRef, useMemo, memo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useChatAI } from "../chat-ai-context";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";
import { TextArea } from "@/components/base/textarea/textarea";
import { Avatar } from "@/components/base/avatar/avatar";
import { shipperQuoteService } from "@/data/mock-quotes";
import { addressBookService } from "@/data/mock-address-book";

interface QuoteRequestWizardProps {
    onCancel: () => void;
}

type Step = "subject" | "body" | "recipients" | "review" | "completed";

// Componente memoizzato per i messaggi dell'assistente
const AssistantMessage = memo(({ question, avatar }: { question: string; avatar: React.ReactElement }) => {
    return (
        <div className="flex items-start">
            {avatar}
            <div className="flex-1">
                <div className="max-w-[520px] rounded-2xl border border-secondary bg-primary px-4 py-3 text-sm leading-6 text-primary shadow-xs">
                    {question}
                </div>
            </div>
        </div>
    );
});

AssistantMessage.displayName = "AssistantMessage";

export const QuoteRequestWizard = ({ onCancel }: QuoteRequestWizardProps) => {
    const { t } = useTranslation(['common', 'pages:quotes']);
    const navigate = useNavigate();
    const { wizardFormData, setWizardFormData, appendMessage, closeDrawer } = useChatAI();
    const listRef = useRef<HTMLDivElement | null>(null);

    const [currentStep, setCurrentStep] = useState<Step>("subject");
    const [freightForwarderOptions, setFreightForwarderOptions] = useState<Array<{ id: string; label: string }>>([]);
    const [selectedRecipients, setSelectedRecipients] = useState<string[]>([]);
    const [inputValue, setInputValue] = useState("");
    const [isLoading, setIsLoading] = useState(false);

    const steps: Step[] = ["subject", "body", "recipients", "review"];

    useEffect(() => {
        const accountId = localStorage.getItem('accountId') || '1';
        const entries = addressBookService.search({
            accountId,
            role: 'Freight Forwarder',
        });

        const options = entries.map(entry => ({
            id: entry.id,
            label: `${entry.firstName} ${entry.lastName} (${entry.email})`,
        }));

        setFreightForwarderOptions(options);
    }, []);

    useEffect(() => {
        if (wizardFormData[currentStep]) {
            setInputValue(String(wizardFormData[currentStep]));
        } else {
            setInputValue("");
        }
        if (currentStep === "recipients" && wizardFormData.recipients) {
            setSelectedRecipients(Array.isArray(wizardFormData.recipients) ? wizardFormData.recipients : []);
        }
    }, [currentStep, wizardFormData]);

    useEffect(() => {
        listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" });
    }, [currentStep]);

    const getStepQuestion = (step: Step): string => {
        switch (step) {
            case "subject":
                return t('aiChat.wizard.quoteRequest.subject');
            case "body":
                return t('aiChat.wizard.quoteRequest.body');
            case "recipients":
                return t('aiChat.wizard.quoteRequest.recipients');
            case "review":
                // Genera il messaggio di review con i dati inseriti
                const recipientsList = selectedRecipients.map(id =>
                    freightForwarderOptions.find(opt => opt.id === id)?.label
                ).filter(Boolean).join(", ");
                
                return t('aiChat.wizard.quoteRequest.review', {
                    subject: wizardFormData.subject || '',
                    body: wizardFormData.body || '',
                    recipients: recipientsList || '',
                });
            default:
                return "";
        }
    };

    const handleAnswer = (value: string | string[]) => {
        const updatedData = { ...wizardFormData, [currentStep]: value };
        setWizardFormData(updatedData);

        const displayValue = Array.isArray(value) ? value.join(", ") : value;
        appendMessage({ role: "user", content: displayValue });

        setTimeout(() => {
            appendMessage({ role: "assistant", content: t('aiChat.wizard.confirmed') });

            const currentIndex = steps.indexOf(currentStep);
            if (currentIndex < steps.length - 1) {
                setTimeout(() => {
                    setCurrentStep(steps[currentIndex + 1]);
                    appendMessage({ role: "assistant", content: getStepQuestion(steps[currentIndex + 1]) });
                }, 500);
            } else {
                setTimeout(() => {
                    setCurrentStep("review");
                    showReview();
                }, 500);
            }
        }, 500);
    };

    const toggleRecipient = (id: string) => {
        const newSelection = selectedRecipients.includes(id)
            ? selectedRecipients.filter(r => r !== id)
            : [...selectedRecipients, id];
        setSelectedRecipients(newSelection);
    };

    const handleRecipientsNext = () => {
        if (selectedRecipients.length > 0) {
            handleAnswer(selectedRecipients);
        }
    };

    const showReview = () => {
        const recipientsList = selectedRecipients.map(id =>
            freightForwarderOptions.find(opt => opt.id === id)?.label
        ).filter(Boolean).join(", ");

        const reviewMessage = t('aiChat.wizard.quoteRequest.review', {
            subject: wizardFormData.subject,
            body: wizardFormData.body,
            recipients: recipientsList,
        });
        appendMessage({ role: "assistant", content: reviewMessage });
    };

    const handleSubmit = async () => {
        setIsLoading(true);
        try {
            const accountId = localStorage.getItem('accountId') || '1';

            if (!wizardFormData.subject?.trim() || !wizardFormData.body?.trim() || selectedRecipients.length === 0) {
                appendMessage({ role: "assistant", content: t('form.errors.allFieldsRequired', { ns: 'common' }) });
                setIsLoading(false);
                return;
            }

            const newRequest = shipperQuoteService.create({
                subject: wizardFormData.subject,
                body: wizardFormData.body,
                recipients: selectedRecipients,
                extractedData: {},
                shipperId: accountId,
            });

            appendMessage({ role: "assistant", content: t('aiChat.wizard.quoteRequest.success') });
            setCurrentStep("completed");

            setTimeout(() => {
                closeDrawer();
                navigate(`/shipper/quotes/${newRequest.id}`);
            }, 2000);
        } catch (error) {
            appendMessage({ role: "assistant", content: t('form.errors.generic', { ns: 'common' }) });
        } finally {
            setIsLoading(false);
        }
    };

    const renderInput = () => {
        if (currentStep === "review") {
            return (
                <div className="space-y-3">
                    <Button onClick={handleSubmit} color="primary" size="lg" isDisabled={isLoading}>
                        {isLoading ? t('form.saving', { ns: 'common' }) : t('aiChat.wizard.create')}
                    </Button>
                    <Button onClick={onCancel} color="secondary" size="lg">
                        {t('buttons.cancel')}
                    </Button>
                </div>
            );
        }

        if (currentStep === "completed") {
            return (
                <div className="text-center text-primary">
                    {t('aiChat.wizard.quoteRequest.completed')}
                </div>
            );
        }

        if (currentStep === "recipients") {
            return (
                <div className="space-y-3">
                    <div className="space-y-2">
                        {freightForwarderOptions.map((option) => (
                            <Button
                                key={option.id}
                                onClick={() => toggleRecipient(option.id)}
                                color={selectedRecipients.includes(option.id) ? "primary" : "secondary"}
                                size="lg"
                                className="w-full"
                            >
                                {option.label}
                            </Button>
                        ))}
                    </div>
                    <div className="flex gap-2">
                        <Button onClick={handleRecipientsNext} color="primary" size="md" isDisabled={selectedRecipients.length === 0}>
                            {t('buttons.next')}
                        </Button>
                        <Button onClick={onCancel} color="secondary" size="md">
                            {t('buttons.cancel')}
                        </Button>
                    </div>
                </div>
            );
        }

        if (currentStep === "body") {
            return (
                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        if (inputValue.trim()) {
                            handleAnswer(inputValue.trim());
                            setInputValue("");
                        }
                    }}
                    className="space-y-3"
                >
                    <TextArea
                        value={inputValue}
                        onChange={setInputValue}
                        placeholder={t('aiChat.wizard.enterValue')}
                        rows={8}
                    />
                    <div className="flex gap-2">
                        <Button type="submit" color="primary" size="md">
                            {t('buttons.next')}
                        </Button>
                        <Button type="button" onClick={onCancel} color="secondary" size="md">
                            {t('buttons.cancel')}
                        </Button>
                    </div>
                </form>
            );
        }

        return (
            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    if (inputValue.trim()) {
                        handleAnswer(inputValue.trim());
                        setInputValue("");
                    }
                }}
                className="space-y-3"
            >
                <Input
                    value={inputValue}
                    onChange={setInputValue}
                    placeholder={t('aiChat.wizard.enterValue')}
                    size="md"
                />
                <div className="flex gap-2">
                    <Button type="submit" color="primary" size="md">
                        {t('buttons.next')}
                    </Button>
                    <Button type="button" onClick={onCancel} color="secondary" size="md">
                        {t('buttons.cancel')}
                    </Button>
                </div>
            </form>
        );
    };

    // Memoizzare l'avatar per evitare che cambi ad ogni render
    const assistantAvatarUrl = useMemo(() => {
        const avatarPool = [
            "https://i.pravatar.cc/80?img=1",
            "https://i.pravatar.cc/80?img=2",
        ];
        return avatarPool[Math.floor(Math.random() * avatarPool.length)];
    }, []); // Solo una volta al mount

    // Memoizzare il componente avatar per evitare re-render inutili
    const AssistantAvatar = useMemo(() => {
        return <Avatar size="sm" src={assistantAvatarUrl} status="online" className="mr-3" />;
    }, [assistantAvatarUrl]);

    // Memoizzare la domanda dello step corrente per evitare re-render quando cambia solo inputValue
    // Per lo step "review", includiamo anche wizardFormData e selectedRecipients nelle dipendenze
    const currentStepQuestion = useMemo(() => {
        return getStepQuestion(currentStep);
    }, [currentStep, t, wizardFormData, selectedRecipients, freightForwarderOptions]);

    return (
        <div className="flex h-full min-h-0 flex-col">
            <div ref={listRef} className="flex-1 overflow-y-auto py-4 md:py-6">
                <div className="flex flex-col gap-5">
                    {/* Mostra il messaggio dell'assistente per tutti gli step, incluso review */}
                    {currentStepQuestion && (
                        <AssistantMessage 
                            question={currentStepQuestion}
                            avatar={AssistantAvatar}
                        />
                    )}

                    {renderInput()}
                </div>
            </div>
        </div>
    );
};

